import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { signIn } from "../api/content";
import { safeNext, sessionName, useSession } from "../session/SessionContext";

/**
 * Member sign-in. Members are people the site owner has given an account to,
 * so they can see photos and videos that are not public. There is no sign-up:
 * accounts are created in the admin console.
 *
 * Success does a full page load to the destination rather than a client-side
 * navigation, so every list and image on the site is fetched afresh with the
 * new session cookie.
 */
export function SignInPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { session } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signIn(email.trim(), password);
      window.location.assign(next);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "That email and password were not accepted."
          : err instanceof Error
            ? err.message
            : "Sign-in failed.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="container">
        <div className="member-box">
          <h1>Member sign-in</h1>
          {session.kind !== "ANONYMOUS" ? (
            <p>
              You're signed in as <strong>{sessionName(session)}</strong>.{" "}
              <Link to="/account">Your account →</Link>
            </p>
          ) : (
            <>
              <p className="muted">
                Some photos and videos are shared only with family and friends. If you've
                been given an account, sign in to see them.
              </p>
              {error && (
                <p className="notice notice--err" role="alert">
                  ✕ {error}
                </p>
              )}
              <form className="form" onSubmit={onSubmit} noValidate>
                <div className="field">
                  <label htmlFor="member-email">Email</label>
                  <input
                    id="member-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label htmlFor="member-password">Password</label>
                  <input
                    id="member-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <button className="btn" type="submit" disabled={busy || !email.trim() || !password}>
                    {busy ? "Checking…" : "Sign in"}
                  </button>
                </div>
              </form>
              <p className="muted" style={{ fontSize: "0.8rem" }}>
                Need access? <Link to="/contact">Get in touch</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
